/** The board books in quarter hours, even though a treatment lasts a full one. */
export const SLOT_MINUTES = 15

/** How long a treatment runs, and so how many slots its tile covers. */
export const APPOINTMENT_MINUTES = 60

/**
 * One row per slot between opening and closing. Both numbers come from the
 * calendar response — the studio's hours are configuration, not a constant,
 * and the backend rejects a booking outside them.
 */
export function buildSlots(
  openingHours: { start: string; end: string },
  slotMinutes: number = SLOT_MINUTES,
): string[] {
  const opens = toMinutes(openingHours.start)
  const closes = toMinutes(openingHours.end)
  const step = slotMinutes > 0 ? slotMinutes : SLOT_MINUTES
  if (closes <= opens) return []

  return Array.from({ length: Math.ceil((closes - opens) / step) }, (_, index) =>
    fromMinutes(opens + index * step),
  )
}

/** `HH:mm` as minutes since midnight, for any arithmetic on times. */
export function toMinutes(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))
}

export function fromMinutes(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)))
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`
}

/** Whether a slot starts a new hour — the only rows the board labels in full. */
export function isFullHour(slot: string): boolean {
  return slot.endsWith(':00')
}

/** Midday avoids every daylight-saving edge when only the date matters. */
function atMidday(iso: string): Date {
  return new Date(`${iso}T12:00:00`)
}

export function toIsoDate(date: Date): string {
  const local = new Date(date)
  local.setHours(12, 0, 0, 0)
  return `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`
}

export function today(): string {
  return toIsoDate(new Date())
}

export function addDays(iso: string, delta: number): string {
  const date = atMidday(iso)
  date.setDate(date.getDate() + delta)
  return toIsoDate(date)
}

/** Monday of the week the date falls in. */
export function startOfWeek(iso: string): string {
  return addDays(iso, -((atMidday(iso).getDay() + 6) % 7))
}

/** Monday to Friday — the studio's week, as the week board shows it. */
export function workWeek(iso: string): string[] {
  const monday = startOfWeek(iso)
  return Array.from({ length: 5 }, (_, index) => addDays(monday, index))
}

const longDate = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

const columnDate = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
})

const weekdayOnly = new Intl.DateTimeFormat('de-DE', { weekday: 'short' })

/** "Donnerstag, 10. September 2026" — the toolbar's heading. */
export function formatLongDate(iso: string): string {
  return longDate.format(atMidday(iso))
}

/** "Do., 10.09." — a week-board column header. */
export function formatColumnDate(iso: string): string {
  return columnDate.format(atMidday(iso))
}

export function formatWeekday(iso: string): string {
  return weekdayOnly.format(atMidday(iso))
}

export function dayOfMonth(iso: string): number {
  return atMidday(iso).getDate()
}

/** Which quarter-hour row a `HH:mm` time belongs to, rounding down. */
export function slotOf(time: string): string {
  const minutes = toMinutes(time)
  return fromMinutes(minutes - (minutes % SLOT_MINUTES))
}

/** One hour after the given `HH:mm`. */
export function oneHourAfter(time: string): string {
  return fromMinutes(toMinutes(time) + APPOINTMENT_MINUTES)
}

/**
 * How many slot rows an appointment covers. Anything shorter than a slot still
 * gets one row, so a tile is never invisible.
 */
export function slotSpan(start: string, end: string): number {
  return Math.max(1, Math.ceil((toMinutes(end) - toMinutes(start)) / SLOT_MINUTES))
}

/**
 * An instant as the studio's own wall clock. The backend resolves times at the
 * location's zone and sends a real offset, but the browser's zone is a
 * different one — two studios in different countries do not share a clock, so
 * nothing here may go through `Date#getHours`.
 */
const zonedFormatters = new Map<string, Intl.DateTimeFormat>()

function zonedFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = zonedFormatters.get(timezone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      // h23 rather than hour12:false — that pair renders midnight as "24".
      hourCycle: 'h23',
    })
    zonedFormatters.set(timezone, formatter)
  }
  return formatter
}

/** `2026-09-10T09:30:00+02:00` in `Europe/Berlin` -> `2026-09-10` and `09:30`. */
export function toZonedParts(instant: string, timezone: string): { date: string; time: string } {
  const parts = zonedFormatter(timezone).formatToParts(new Date(instant))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? '00'

  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  }
}

function zoneOffsetMinutes(instant: number, timezone: string): number {
  const parts = zonedFormatter(timezone).formatToParts(new Date(instant))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((candidate) => candidate.type === type)?.value ?? 0)

  const asIfUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
  )
  // The formatter drops seconds, so compare against a whole minute.
  return Math.round((asIfUtc - Math.floor(instant / 60_000) * 60_000) / 60_000)
}

export function toOffsetDateTime(date: string, time: string, timezone: string): string {
  const wallClock = Date.parse(`${date}T${time}:00Z`)

  let offset = zoneOffsetMinutes(wallClock, timezone)
  offset = zoneOffsetMinutes(wallClock - offset * 60_000, timezone)

  const sign = offset < 0 ? '-' : '+'
  const size = Math.abs(offset)
  const hh = String(Math.floor(size / 60)).padStart(2, '0')
  const mm = String(size % 60).padStart(2, '0')

  return `${date}T${time}:00${sign}${hh}:${mm}`
}
